import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AppShell } from '@/components/layout/AppShell'
import { AddAccountModal } from '@/components/positions/AddAccountModal'
import { AddPositionModal } from '@/components/positions/AddPositionModal'
import { StopConfigModal } from '@/components/positions/StopConfigModal'
import { AddExternalAssetModal } from '@/components/externalAssets/AddExternalAssetModal'
import { ExternalAssetList } from '@/components/externalAssets/ExternalAssetList'
import { WhatIfModal } from '@/components/whatIf/WhatIfModal'
import { listAccounts } from '@/lib/accountsApi'
import { formatNumberDe, formatPct } from '@/lib/format'
import { deletePosition, listPositions } from '@/lib/positionsApi'
import {
  formatPnl,
  formatPnlAmount,
  formatPnlPct,
  pnlTone,
  positionValue,
  sumPnlEur,
} from '@/lib/pnl'
import { getPrice } from '@/lib/pricesApi'
import { getStopStatus } from '@/lib/stopApi'
import type { Position } from '@/types/position'
import type { StopMode } from '@/types/stop'

const PRICE_STALE_MS = 15 * 60 * 1000

const PNL_TONE_TEXT = {
  gain: 'text-gain',
  loss: 'text-loss',
  neutral: 'text-ink',
} as const

function PnlCell({ pos }: { pos: Position }) {
  return (
    <p className={PNL_TONE_TEXT[pnlTone(pos.pnl_abs)]}>
      {formatPnl(pos.pnl_abs, pos.pnl_pct, pos.currency)}
    </p>
  )
}

/** Zweizeilig (Betrag / Prozent) für die schmale Desktop-Spalte. */
function PnlCellStacked({ pos }: { pos: Position }) {
  const tone = PNL_TONE_TEXT[pnlTone(pos.pnl_abs)]
  const pctText = formatPnlPct(pos.pnl_pct)
  return (
    <>
      <p className={tone}>{formatPnlAmount(pos.pnl_abs, pos.currency)}</p>
      <p className={`mt-1 text-xs ${pctText === null ? 'text-muted' : tone}`}>{pctText ?? 'G/V'}</p>
    </>
  )
}

function PriceCell({ isin }: { isin: string }) {
  const query = useQuery({
    queryKey: ['price', isin],
    queryFn: () => getPrice(isin),
    staleTime: PRICE_STALE_MS,
    retry: false,
  })

  if (query.isLoading) {
    return <p className="text-faint">…</p>
  }
  if (!query.data) {
    return <p className="text-faint">—</p>
  }
  return <p className="text-ink">{formatNumberDe(query.data.price, 2)}</p>
}

const MODE_STYLE: Record<StopMode, { dot: string; label: string; text: string }> = {
  inactive: { dot: 'bg-faint', label: 'Kein Stop', text: 'text-faint' },
  no_peak: { dot: 'bg-bernstein-dim', label: 'Ausstehend', text: 'text-muted' },
  armed: { dot: 'bg-gain', label: 'Aktiv', text: 'text-gain' },
  near_hit: { dot: 'bg-bernstein', label: 'Nah am Stop', text: 'text-bernstein' },
  triggered: { dot: 'bg-loss', label: 'Getriggert', text: 'text-loss' },
}

function StopCell({ pos, onEdit }: { pos: Position; onEdit: () => void }) {
  const query = useQuery({
    queryKey: ['stopStatus', pos.id],
    queryFn: () => getStopStatus(pos.id),
    staleTime: PRICE_STALE_MS,
  })

  const style = query.data ? MODE_STYLE[query.data.mode] : null
  const distance = query.data?.distance_pct
  const distanceText = distance ? `−${formatPct(distance)}` : null

  return (
    <button
      type="button"
      onClick={onEdit}
      className="flex w-full items-center justify-end gap-2 text-right transition-opacity hover:opacity-80"
      aria-label="Stop-Loss konfigurieren"
    >
      {query.isLoading || !style ? (
        <span className="text-faint">…</span>
      ) : (
        <>
          <span className={`size-1.5 rounded-full ${style.dot}`} aria-hidden />
          <span className={`text-xs ${style.text}`}>{distanceText ?? style.label}</span>
        </>
      )}
    </button>
  )
}

export function PositionsPage() {
  const queryClient = useQueryClient()
  const [showAddPosition, setShowAddPosition] = useState(false)
  const [showAddAccount, setShowAddAccount] = useState(false)
  const [showAddExternalAsset, setShowAddExternalAsset] = useState(false)
  const [stopEditPos, setStopEditPos] = useState<Position | null>(null)
  const [whatIfPositionId, setWhatIfPositionId] = useState<number | null>(null)

  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: listAccounts,
  })
  const positionsQuery = useQuery({
    queryKey: ['positions'],
    queryFn: listPositions,
  })

  const deleteMutation = useMutation({
    mutationFn: deletePosition,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['positions'] }),
  })

  const handleDelete = (pos: Position) => {
    if (!confirm(`"${pos.name}" mit allen Tranchen löschen?`)) return
    deleteMutation.mutate(pos.id)
  }

  const positions = positionsQuery.data ?? []
  const accounts = accountsQuery.data ?? []
  const totals = sumPnlEur(positions)

  return (
    <AppShell>
      <article className="fade-in max-w-6xl">
        <header className="flex flex-col items-stretch gap-3 md:flex-row md:items-end md:justify-between md:gap-6">
          <div>
            <p className="tracked-caps text-[10px] text-faint">Verwaltung</p>
            <h2 className="mt-2 text-2xl text-ink">Positionen</h2>
            <p className="mt-1 font-mono text-xs text-muted">
              {positions.length} {positions.length === 1 ? 'Position' : 'Positionen'} ·{' '}
              {accounts.length} {accounts.length === 1 ? 'Account' : 'Accounts'}
            </p>
            {totals.pnlEur !== null && (
              <p className="mt-1 font-mono text-xs text-muted" data-numeric>
                Wert {formatNumberDe(totals.valueEur)} € · G/V{' '}
                <span className={PNL_TONE_TEXT[pnlTone(String(totals.pnlEur))]}>
                  {formatPnl(
                    String(totals.pnlEur),
                    totals.pct === null ? null : String(totals.pct),
                    'EUR',
                  )}
                </span>
                {totals.incomplete && <span className="text-faint"> · unvollständig</span>}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setShowAddAccount(true)}
              className="border border-hairline px-4 py-2 font-mono text-xs text-muted transition-colors hover:border-bernstein hover:text-bernstein"
            >
              + Account
            </button>
            <button
              type="button"
              onClick={() => setShowAddPosition(true)}
              className="tracked-caps bg-bernstein px-5 py-2.5 text-[11px] text-void transition-colors hover:bg-bernstein-dim"
            >
              + Position
            </button>
          </div>
        </header>

        <div className="mt-10">
          {positionsQuery.isLoading ? (
            <p className="font-mono text-sm text-muted">Lade Positionen…</p>
          ) : positionsQuery.isError ? (
            <div role="alert" className="border border-loss/40 bg-loss/5 px-4 py-3">
              <p className="tracked-caps text-[10px] text-loss">Fehler</p>
              <p className="mt-2 font-mono text-sm text-ink">
                Positionen konnten nicht geladen werden.
              </p>
              <p className="mt-1 font-mono text-xs text-muted">{positionsQuery.error.message}</p>
            </div>
          ) : positions.length === 0 ? (
            <div className="border border-hairline bg-panel px-6 py-12 text-center">
              <p className="tracked-caps text-[10px] text-faint">Leer</p>
              <p className="mt-3 text-sm text-muted">Noch keine Positionen angelegt.</p>
              <p className="mt-2 font-mono text-xs text-faint">
                {accounts.length === 0
                  ? 'Zuerst einen Broker-Account anlegen, dann Positionen eingeben.'
                  : 'Mit „+ Position" oben rechts starten.'}
              </p>
            </div>
          ) : (
            <div className="flex flex-col">
              {positions.map((pos) => (
                <div key={pos.id} className="hairline-b">
                  {/* Desktop-Row (>=md) — existing 5-column layout */}
                  <div className="hidden items-baseline gap-3 py-4 font-mono text-sm md:flex">
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-ink">{pos.name}</p>
                      <p className="mt-1 text-xs text-muted">
                        {pos.isin} · {pos.ticker ?? '—'} · {pos.currency}
                      </p>
                    </div>
                    <div className="w-24 text-right" data-numeric>
                      <p className="text-ink">{formatNumberDe(pos.total_quantity, 4)}</p>
                      <p className="mt-1 text-xs text-muted">
                        {pos.tax_lots.length} {pos.tax_lots.length === 1 ? 'Tranche' : 'Tranchen'}
                      </p>
                    </div>
                    <div className="w-28 text-right" data-numeric>
                      <p className="text-ink">{formatNumberDe(pos.avg_cost)}</p>
                      <p className="mt-1 text-xs text-muted">ø Kosten</p>
                    </div>
                    <div className="w-28 text-right" data-numeric>
                      <PriceCell isin={pos.isin} />
                      <p className="mt-1 text-xs text-muted">Aktuell</p>
                    </div>
                    <div className="w-32 text-right" data-numeric>
                      <StopCell pos={pos} onEdit={() => setStopEditPos(pos)} />
                      <p className="mt-1 text-xs text-muted">Stop</p>
                    </div>
                    <div className="w-28 text-right" data-numeric>
                      <p className="text-ink">{formatNumberDe(pos.cost_basis)}</p>
                      <p className="mt-1 text-xs text-muted">Einstand</p>
                    </div>
                    <div className="w-28 text-right" data-numeric>
                      <p className="text-ink">
                        {formatNumberDe(positionValue(pos.total_quantity, pos.current_price))}
                      </p>
                      <p className="mt-1 text-xs text-muted">Wert</p>
                    </div>
                    <div className="w-28 text-right" data-numeric>
                      <PnlCellStacked pos={pos} />
                    </div>
                    <button
                      type="button"
                      onClick={() => setWhatIfPositionId(pos.id)}
                      className="ml-2 text-xs text-muted hover:text-ink"
                    >
                      What-if
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(pos)}
                      disabled={deleteMutation.isPending}
                      className="ml-2 text-xs text-faint transition-colors hover:text-loss disabled:opacity-40"
                    >
                      Löschen
                    </button>
                  </div>

                  {/* Mobile-Card (<md) */}
                  <div className="flex flex-col gap-2 px-1 py-3 font-mono text-sm md:hidden">
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="flex-1 min-w-0 truncate text-ink">{pos.name}</p>
                      <div className="text-right shrink-0" data-numeric>
                        <PriceCell isin={pos.isin} />
                      </div>
                    </div>
                    <div className="flex items-baseline justify-between gap-3 text-xs">
                      <span className="text-muted">G/V</span>
                      <div className="text-right shrink-0" data-numeric>
                        <PnlCell pos={pos} />
                      </div>
                    </div>
                    <div className="flex items-baseline justify-between gap-3 text-xs">
                      <p className="flex-1 min-w-0 truncate text-muted">
                        {pos.ticker ?? pos.isin} · {pos.currency}
                      </p>
                      <div className="text-right shrink-0" data-numeric>
                        <StopCell pos={pos} onEdit={() => setStopEditPos(pos)} />
                      </div>
                    </div>
                    <div className="flex items-baseline justify-between gap-3 text-xs text-muted">
                      <span>
                        {formatNumberDe(pos.total_quantity, 4)} · ø {formatNumberDe(pos.avg_cost)}
                      </span>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setWhatIfPositionId(pos.id)}
                          className="text-muted hover:text-ink"
                        >
                          What-if
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(pos)}
                          disabled={deleteMutation.isPending}
                          className="text-faint transition-colors hover:text-loss disabled:opacity-40"
                        >
                          Löschen
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <section className="hairline-t mt-20 pt-12">
          <header className="mb-6 flex flex-col items-stretch gap-3 md:flex-row md:items-end md:justify-between md:gap-6">
            <div>
              <p className="tracked-caps text-[10px] text-faint">Off-Broker</p>
              <h2 className="mt-2 text-xl text-ink">External Assets</h2>
              <p className="mt-1 max-w-2xl font-mono text-xs text-muted">
                Werte ausserhalb der Broker (BTC via CoinTracking, Immobilien, physisches Gold).
                Manuell gepflegt — Empfehlung 1×/Monat aktualisieren.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowAddExternalAsset(true)}
              className="tracked-caps bg-bernstein px-5 py-2.5 text-[11px] text-void transition-colors hover:bg-bernstein-dim"
            >
              + External Asset
            </button>
          </header>
          <ExternalAssetList detailed />
        </section>
      </article>

      <AddAccountModal open={showAddAccount} onClose={() => setShowAddAccount(false)} />
      <AddExternalAssetModal
        open={showAddExternalAsset}
        onClose={() => setShowAddExternalAsset(false)}
      />
      <AddPositionModal
        open={showAddPosition}
        onClose={() => setShowAddPosition(false)}
        accounts={accounts}
        onNeedAccount={() => {
          setShowAddPosition(false)
          setShowAddAccount(true)
        }}
      />
      <StopConfigModal
        open={stopEditPos !== null}
        onClose={() => setStopEditPos(null)}
        position={stopEditPos}
      />
      <WhatIfModal
        open={whatIfPositionId !== null}
        onClose={() => setWhatIfPositionId(null)}
        initialPositionId={whatIfPositionId}
      />
    </AppShell>
  )
}
